import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import ReadingProgress from './ReadingProgress';
import './ReadingTray.css';

/**
 * App-wide "still being read" tray.
 *
 * A progress window lives in the browser: close it or refresh and the read it was
 * showing is out of sight, while the document goes on being read in the
 * background. This sits beside the notification bell on every screen, lists the
 * person's own reads still in progress (the server answers only for them), and
 * takes them back to the screen that shows the full progress.
 *
 * Screens announce a read they have just started with announceReadingStarted(),
 * so the tray appears straight away instead of on its next check.
 */
export const READING_STARTED_EVENT = 'hms:reading-started';

export const announceReadingStarted = () => {
    try {
        window.dispatchEvent(new Event(READING_STARTED_EVENT));
    } catch (e) {
        // Older browsers without the Event constructor: the next check finds it.
    }
};

const CHECK_EVERY_MS = 8000;
// How long a finished read stays in the tray with its way back.
const FINISHED_SHOWN_MS = 30000;

const pageText = (progress) => {
    if (progress?.page && progress?.pages) return `page ${progress.page} of ${progress.pages}`;
    if (progress?.pages) return `all ${progress.pages} pages`;
    return '';
};

const ReadingTray = () => {
    const history = useHistory();
    const [items, setItems] = useState([]);
    const [finished, setFinished] = useState([]);
    const [open, setOpen] = useState(false);
    const previous = useRef([]);
    const checking = useRef(false);

    const check = useCallback(async () => {
        if (checking.current) return;
        checking.current = true;
        try {
            const response = await axiosInstance.get('/api/reading-progress/mine/');
            const next = response.data?.items || [];
            const stillGoing = new Set(next.map((item) => item.key));
            const done = previous.current.filter((item) => !stillGoing.has(item.key));
            if (done.length) {
                const at = Date.now();
                setFinished((current) => [...done.map((item) => ({ ...item, finishedAt: at })), ...current].slice(0, 5));
            }
            previous.current = next;
            setItems(next);
        } catch (error) {
            // Not logged in as staff, or a blip: show nothing rather than an error.
            if ([401, 403].includes(error?.response?.status)) {
                previous.current = [];
                setItems([]);
            }
        } finally {
            checking.current = false;
        }
    }, []);

    // Check on arrival, when a screen starts a read, and when the tab comes back.
    useEffect(() => {
        check();
        const onStarted = () => { setTimeout(check, 1500); };
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        window.addEventListener(READING_STARTED_EVENT, onStarted);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener(READING_STARTED_EVENT, onStarted);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [check]);

    // Keep checking only while something is being read.
    useEffect(() => {
        if (!items.length) return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') check();
        }, CHECK_EVERY_MS);
        return () => clearInterval(timer);
    }, [items.length, check]);

    // Let finished reads drop out after a while.
    useEffect(() => {
        if (!finished.length) return undefined;
        const timer = setInterval(() => {
            setFinished((current) => current.filter((item) => Date.now() - item.finishedAt < FINISHED_SHOWN_MS));
        }, 5000);
        return () => clearInterval(timer);
    }, [finished.length]);

    if (!items.length && !finished.length) return null;

    const goTo = (link) => {
        setOpen(false);
        history.push(link);
    };

    const lead = items[0];
    const label = items.length
        ? (items.length === 1
            ? ['Reading', pageText(lead.progress)].filter(Boolean).join(' · ')
            : `Reading ${items.length} documents`)
        : 'Finished reading';

    return (
        <div className="reading-tray">
            {open && (
                <div className="reading-tray-panel" role="dialog" aria-label="Documents being read">
                    <div className="reading-tray-panel-head">
                        <strong>Documents being read</strong>
                        <button type="button" className="reading-tray-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
                    </div>
                    {items.map((item) => (
                        <div key={item.key} className="reading-tray-item">
                            <div className="reading-tray-item-head">
                                <span className="reading-tray-item-title">{item.title}</span>
                                <span className="reading-tray-item-place">{item.place}</span>
                            </div>
                            <ReadingProgress compact progress={item.progress} />
                            <button type="button" className="reading-tray-open" onClick={() => goTo(item.link)}>
                                Show progress
                            </button>
                        </div>
                    ))}
                    {finished.map((item) => (
                        <div key={`${item.key}:done`} className="reading-tray-item is-finished">
                            <div className="reading-tray-item-head">
                                <span className="reading-tray-item-title">{item.title}</span>
                                <span className="reading-tray-item-place">{item.place}</span>
                            </div>
                            <div className="reading-tray-finished">Finished — open it to see the result.</div>
                            <button type="button" className="reading-tray-open" onClick={() => goTo(item.link)}>
                                Open
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <button
                type="button"
                className={`reading-tray-pill${items.length ? '' : ' is-finished'}`}
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
            >
                {items.length > 0 && <span className="reading-tray-dot" aria-hidden="true" />}
                {label}
            </button>
        </div>
    );
};

export default ReadingTray;
